import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
    MATCH_MODES,
    MAX_RULES,
    getFieldsForType,
    getFieldDef,
    getOperatorsForValueType,
    operatorNeedsSecondValue,
    operatorUsesListInput,
    operatorTakesNoValue,
    OPERATOR_LABEL_KEYS,
    OPERATOR_LABEL_DEFAULTS,
    validateRule,
} from '../../../utils/discoverLayerFilterSchema';
import './LayerWizardStepFilters.scss';

/**
 * Wizard Step 2 — Dynamic filter builder.
 *
 * Renders match-mode toggle, condition rows with field/operator/value
 * pickers, add/remove controls, and inline validation.
 */
export default function LayerWizardStepFilters({
    objectType,
    matchMode,
    rules,
    ruleErrors,
    prunedRuleCount = 0,
    onMatchModeChange,
    onAddRule,
    onUpdateRule,
    onRemoveRule,
    onValidityChange,
    onDismissPruneNotice = undefined,
}) {
    const { t } = useTranslation();
    const fields = useMemo(() => getFieldsForType(objectType), [objectType]);

    /** Track which rules have been blurred for inline error display. */
    const [blurredRules, setBlurredRules] = useState({});

    const markRuleBlurred = useCallback((ruleId) => {
        setBlurredRules((prev) => ({ ...prev, [ruleId]: true }));
    }, []);

    // Report validity to wizard whenever rules change.
    const isValid = useMemo(() => {
        if (rules.length === 0) return true; // empty ruleset is valid
        return rules.every((r) => validateRule(r, objectType) === null);
    }, [rules, objectType]);

    useEffect(() => {
        onValidityChange(isValid);
    }, [isValid, onValidityChange]);

    const atLimit = rules.length >= MAX_RULES;

    return (
        <div className="layer-wizard-filters">
            <h3 className="layer-wizard-filters__title">
                {t('discoverPage.layerWizard.filters.sectionTitle', 'Filter rules')}
            </h3>

            {/* Prune notice — shown when type switch removed incompatible rules */}
            {prunedRuleCount > 0 && (
                <div className="layer-wizard-filters__prune-notice" role="status">
                    <p className="layer-wizard-filters__prune-notice-text">
                        {t('discoverPage.layerWizard.filters.pruneNotice', {
                            count: prunedRuleCount,
                            defaultValue: '{{count}} rule(s) were removed because they are not compatible with the selected object type.',
                        })}
                    </p>
                    <button
                        type="button"
                        className="layer-wizard-filters__prune-notice-dismiss"
                        onClick={onDismissPruneNotice}
                        aria-label={t('discoverPage.layerWizard.filters.pruneNoticeDismiss', 'Dismiss')}
                    >
                        &times;
                    </button>
                </div>
            )}

            {/* Match mode toggle */}
            <div className="layer-wizard-filters__match-row">
                <span className="layer-wizard-filters__match-label">
                    {t('discoverPage.layerWizard.filters.matchLabel', 'Match mode')}
                </span>
                <div className="layer-wizard-filters__match-toggle" role="radiogroup">
                    <button
                        type="button"
                        role="radio"
                        aria-checked={matchMode === MATCH_MODES.ALL}
                        className={`layer-wizard-filters__match-btn${matchMode === MATCH_MODES.ALL ? ' layer-wizard-filters__match-btn--active' : ''}`}
                        onClick={() => onMatchModeChange(MATCH_MODES.ALL)}
                    >
                        {t('discoverPage.layerWizard.filters.matchAll', 'All rules (AND)')}
                    </button>
                    <button
                        type="button"
                        role="radio"
                        aria-checked={matchMode === MATCH_MODES.ANY}
                        className={`layer-wizard-filters__match-btn${matchMode === MATCH_MODES.ANY ? ' layer-wizard-filters__match-btn--active' : ''}`}
                        onClick={() => onMatchModeChange(MATCH_MODES.ANY)}
                    >
                        {t('discoverPage.layerWizard.filters.matchAny', 'Any rule (OR)')}
                    </button>
                </div>
            </div>

            {/* Rules list */}
            {rules.length === 0 ? (
                <p className="layer-wizard-filters__empty">
                    {t('discoverPage.layerWizard.filters.emptyState',
                        'No rules yet. This layer will match all markers of the selected object type.')}
                </p>
            ) : (
                <ol className="layer-wizard-filters__rules">
                    {rules.map((rule, idx) => (
                        <FilterRuleRow
                            key={rule.id}
                            rule={rule}
                            index={idx}
                            objectType={objectType}
                            fields={fields}
                            error={blurredRules[rule.id] ? ruleErrors[idx] : null}
                            onUpdate={onUpdateRule}
                            onRemove={onRemoveRule}
                            onBlur={() => markRuleBlurred(rule.id)}
                            t={t}
                        />
                    ))}
                </ol>
            )}

            {/* Add rule button */}
            <button
                type="button"
                className="layer-wizard-filters__add-btn"
                onClick={onAddRule}
                disabled={atLimit}
                title={atLimit ? t('discoverPage.layerWizard.filters.errors.ruleLimit', { max: MAX_RULES }) : undefined}
            >
                {t('discoverPage.layerWizard.actions.addRule', 'Add rule')}
            </button>

            {atLimit && (
                <p className="layer-wizard-filters__limit-warning">
                    {t('discoverPage.layerWizard.filters.errors.ruleLimit', {
                        max: MAX_RULES,
                        defaultValue: 'You can add up to {{max}} rules.',
                    })}
                </p>
            )}
        </div>
    );
}

LayerWizardStepFilters.propTypes = {
    objectType: PropTypes.string.isRequired,
    matchMode: PropTypes.oneOf([MATCH_MODES.ALL, MATCH_MODES.ANY]).isRequired,
    rules: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        fieldId: PropTypes.string.isRequired,
        operator: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        secondValue: PropTypes.string.isRequired,
    })).isRequired,
    ruleErrors: PropTypes.object.isRequired,
    prunedRuleCount: PropTypes.number,
    onMatchModeChange: PropTypes.func.isRequired,
    onAddRule: PropTypes.func.isRequired,
    onUpdateRule: PropTypes.func.isRequired,
    onRemoveRule: PropTypes.func.isRequired,
    onValidityChange: PropTypes.func.isRequired,
    onDismissPruneNotice: PropTypes.func,
};

// ---------------------------------------------------------------------------
// FilterRuleRow (internal)
// ---------------------------------------------------------------------------

function FilterRuleRow({ rule, index, objectType, fields, error = null, onUpdate, onRemove, onBlur, t }) {
    const fieldDef = getFieldDef(objectType, rule.fieldId);
    const operators = useMemo(
        () => (fieldDef ? getOperatorsForValueType(fieldDef.valueType) : []),
        [fieldDef],
    );

    const handleFieldChange = useCallback((e) => {
        const newFieldId = e.target.value;
        // Reset operator and values when field changes
        onUpdate(rule.id, { fieldId: newFieldId, operator: '', value: '', secondValue: '' });
    }, [rule.id, onUpdate]);

    const handleOperatorChange = useCallback((e) => {
        const newOp = e.target.value;
        // Reset values when operator changes
        onUpdate(rule.id, { operator: newOp, value: '', secondValue: '' });
    }, [rule.id, onUpdate]);

    const handleValueChange = useCallback((e) => {
        onUpdate(rule.id, { value: e.target.value });
    }, [rule.id, onUpdate]);

    const handleSecondValueChange = useCallback((e) => {
        onUpdate(rule.id, { secondValue: e.target.value });
    }, [rule.id, onUpdate]);

    const handleRemove = useCallback(() => {
        onRemove(rule.id);
    }, [rule.id, onRemove]);

    const showValue = rule.operator && !operatorTakesNoValue(rule.operator);
    const showSecondValue = operatorNeedsSecondValue(rule.operator);
    const isList = operatorUsesListInput(rule.operator);
    const isEnum = fieldDef?.valueType === 'ENUM';
    const isNumber = fieldDef?.valueType === 'NUMBER';
    const isDateTime = fieldDef?.valueType === 'DATE_TIME';

    const errorMessage = error
        ? t(`discoverPage.layerWizard.filters.errors.${error}`, {
            max: 20,
            defaultValue: error,
        })
        : null;

    return (
        <li className="layer-wizard-filters__rule">
            <div className="layer-wizard-filters__rule-fields">
                {/* Field picker */}
                <select
                    className="layer-wizard-filters__select"
                    value={rule.fieldId}
                    onChange={handleFieldChange}
                    onBlur={onBlur}
                    aria-label={t('discoverPage.layerWizard.filters.fieldLabel', 'Field')}
                >
                    <option value="" disabled>
                        {t('discoverPage.layerWizard.filters.fieldLabel', 'Field')}
                    </option>
                    {fields.map((f) => (
                        <option key={f.id} value={f.id}>
                            {t(f.labelKey, f.labelDefault)}
                        </option>
                    ))}
                </select>

                {/* Operator picker */}
                <select
                    className="layer-wizard-filters__select"
                    value={rule.operator}
                    onChange={handleOperatorChange}
                    onBlur={onBlur}
                    disabled={!rule.fieldId}
                    aria-label={t('discoverPage.layerWizard.filters.operatorLabel', 'Operator')}
                >
                    <option value="" disabled>
                        {t('discoverPage.layerWizard.filters.operatorLabel', 'Operator')}
                    </option>
                    {operators.map((op) => (
                        <option key={op} value={op}>
                            {t(OPERATOR_LABEL_KEYS[op], OPERATOR_LABEL_DEFAULTS[op])}
                        </option>
                    ))}
                </select>

                {/* Value input(s) */}
                {showValue && isEnum && !isList && (
                    <select
                        className="layer-wizard-filters__select"
                        value={rule.value}
                        onChange={handleValueChange}
                        onBlur={onBlur}
                        aria-label={t('discoverPage.layerWizard.filters.valueLabel', 'Value')}
                    >
                        <option value="" disabled>
                            {t('discoverPage.layerWizard.filters.valueLabel', 'Value')}
                        </option>
                        {(fieldDef.enumValues || []).map((ev) => (
                            <option key={ev} value={ev}>{ev}</option>
                        ))}
                    </select>
                )}

                {showValue && !isEnum && (
                    <input
                        className="layer-wizard-filters__input"
                        type={isNumber ? 'number' : isDateTime ? 'date' : 'text'}
                        value={rule.value}
                        onChange={handleValueChange}
                        onBlur={onBlur}
                        placeholder={isList
                            ? t('discoverPage.layerWizard.filters.inValuesHint', 'Separate multiple values with commas.')
                            : t('discoverPage.layerWizard.filters.valueLabel', 'Value')}
                        aria-label={t('discoverPage.layerWizard.filters.valueLabel', 'Value')}
                    />
                )}

                {showValue && isList && isEnum && (
                    <input
                        className="layer-wizard-filters__input"
                        type="text"
                        value={rule.value}
                        onChange={handleValueChange}
                        onBlur={onBlur}
                        placeholder={t('discoverPage.layerWizard.filters.inValuesHint', 'Separate multiple values with commas.')}
                        aria-label={t('discoverPage.layerWizard.filters.valueLabel', 'Value')}
                    />
                )}

                {showSecondValue && (
                    <input
                        className="layer-wizard-filters__input"
                        type={isNumber ? 'number' : isDateTime ? 'date' : 'text'}
                        value={rule.secondValue}
                        onChange={handleSecondValueChange}
                        onBlur={onBlur}
                        placeholder={t('discoverPage.layerWizard.filters.secondValueLabel', 'Second value')}
                        aria-label={t('discoverPage.layerWizard.filters.secondValueLabel', 'Second value')}
                    />
                )}

                {/* Remove button */}
                <button
                    type="button"
                    className="layer-wizard-filters__remove-btn"
                    onClick={handleRemove}
                    aria-label={t('discoverPage.layerWizard.actions.removeRuleAria', {
                        index: index + 1,
                        defaultValue: 'Remove rule {{index}}',
                    })}
                    title={t('discoverPage.layerWizard.actions.removeRuleAria', {
                        index: index + 1,
                        defaultValue: 'Remove rule {{index}}',
                    })}
                >
                    &times;
                </button>
            </div>

            {errorMessage && (
                <p className="layer-wizard-filters__rule-error" role="alert">
                    {errorMessage}
                </p>
            )}
        </li>
    );
}

FilterRuleRow.propTypes = {
    rule: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    objectType: PropTypes.string.isRequired,
    fields: PropTypes.array.isRequired,
    error: PropTypes.string,
    onUpdate: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onBlur: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
};
